import {Injectable, Optional} from '@angular/core';
import {BucketStore} from '../base/bucket/bucket.store';
import {EditorRoutingService} from './editor-routing.service';
import {filter, map, switchMap, take, tap} from 'rxjs/operators';
import {BlockStore} from '@blocks/block/block.store';
import {Block, BlockType, BucketType, Page, Partial} from '@paperlessio/sdk/api/models';
import {BlockSerializer} from '@paperlessio/sdk/api/serializers';
import {Observable} from 'rxjs';
import {ScrollingContainer} from '@shared/scrolling/scrolling-container.enum';
import {ScrollingService} from '@shared/scrolling/scrolling.service';
import {BlockMutationResult} from '@blocks/block/block-mutation.type';
import {BlockService} from '@blocks/block/block.service';

@Injectable()
export class EditorService {
  clipboardText: string;

  constructor(private blockStore: BlockStore,
              public bucketStore: BucketStore,
              private blockService: BlockService,
              private scrollingService: ScrollingService,
              @Optional() private editorRoutingService: EditorRoutingService) {
  }

  duplicate(block: Block) {
    this.blockStore.duplicateBlock(block, block.parent, block.position + 1).subscribe();
  }

  addToBottom(blocks: Block[]): Observable<number> {
    let block_id = 0;

    if (this.bucketStore.bucketType !== BucketType.Partial) {
      block_id = +this.editorRoutingService.selectedPage;
    } else {
      block_id = +this.blockStore.blocks.value.filter(x => x.type === BlockType.Group)[0]?.id;
    }

    return this.blockStore.get(block_id).pipe(
      filter((page: Page) => !page.overlayMode),
      switchMap(page => {
        const children = page.children;
        const position = children.length && children[children.length - 1].position + 1;
        const serializedBlocks = blocks.map((block) => {
          block.parent = block.parent ?? (block.parent_local_uuid ? blocks.find((b) => { return b.local_uuid === block.parent_local_uuid }) : page);
          block.position = block.position ?? position;
          return new BlockSerializer().fromJson(block)
        });
        return this.blockStore.addBlocks(serializedBlocks);
      }),
      tap(async (blockMutationResult: BlockMutationResult) =>
        // TODO: get list with local_uuids to ids map from server and search for the "primary" block
        await this.editorRoutingService.navigateToBlock(blockMutationResult.created_blocks[0]?.id)
      ),
      map((blockMutationResult: BlockMutationResult) => blockMutationResult.created_blocks[0]?.id),
      tap(bid => setTimeout(() => this.scrollToBlock(bid), 100)),
      take(1)
    );
  }

  // TODO: get rid of redundancy. Basically only the code in the first switchMap changes between addToBottom and addPartialToBotton
  addPartialToBottom(partial: Partial): Observable<number> {
    let block_id = 0;

    // This should only happen when a partial is added in a partial
    if (this.bucketStore.bucketType !== BucketType.Partial) {
      block_id = +this.editorRoutingService.selectedPage;
    } else {
      block_id = +this.blockStore.blocks.value.filter(x => x.type === BlockType.Group)[0]?.id;
    }

    return this.blockStore.get(block_id).pipe(
      filter((page: Page) => !page.overlayMode),
      switchMap(page => {
        const children = page.children;
        const position = children.length && children[children.length - 1].position + 1;

        return this.blockStore.duplicatePartialContentAndSend(partial, page, position);
      }),
      tap(async (blockMutationResult: BlockMutationResult) =>
        // TODO: get list with local_uuids to ids map from server and search for the "primary" block
        await this.editorRoutingService.navigateToBlock(blockMutationResult.created_blocks[0]?.id)
      ),
      map((blockMutationResult: BlockMutationResult) => blockMutationResult.created_blocks[0]?.id),
      tap(bid => setTimeout(() => this.scrollToBlock(bid), 100)),
      take(1)
    );
  }

  delete(b: Block) {
    this.blockStore.deleteBlock(b).subscribe();
  }

  deleteSelectOption(b: Block) {
    this.blockStore.deleteSelectOption(b).subscribe();
  }

  scrollToBlock(blockId: number) {
    // Address the main block-instance, no instance from any sidebar etc.
    const id = `block-${blockId}`;
    const target = document.getElementById(id);
    this.scrollingService.scrollToTarget(ScrollingContainer.editor, target, false);
  }
}

function isPartial(partial: Block | Partial): partial is Partial {
  return !partial.hasOwnProperty('type');
}
